import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Card, 
  Descriptions, 
  Image, 
  Tag, 
  Spin, 
  Typography,
  Space,
  Row,
  Col,
  Breadcrumb,
  Modal,
  Form,
  Input,
  InputNumber,
  Divider,
  Empty,
  Tooltip,
  Popconfirm,
  Select,
  List,
  Avatar
} from 'antd';
import { 
  ArrowLeftOutlined, 
  PlusOutlined, 
  EyeOutlined, 
  EditOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  UserOutlined
} from '@ant-design/icons';
import { lifetreeApi } from '../api/lifetreeApi';
import { 
  CharacterDetailResponse, 
  CharacterStateWithRelationships, 
  CreateCharacterStateRequest,
  KeyValuePair,
  UpdateCharacterRequest,
  CreateCharacterStateCompletionRequest,
  CreateCharacterStateCompletionResponse
} from '../api/models/Lifetree';
import { useToast } from '../components/Toast';
import { 
  MethodSelectionModal,
  AIDescriptionModal,
  CreateStateModal
} from '../components/CreateCharacterStateModals';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface UpdateCharacterModalProps {
  isOpen: boolean;
  onClose: () => void;
  character: CharacterDetailResponse | null;
  onSubmit: (data: UpdateCharacterRequest) => Promise<void>;
  isLoading: boolean;
}

const UpdateCharacterModal: React.FC<UpdateCharacterModalProps> = ({ 
  isOpen, 
  onClose, 
  character, 
  onSubmit, 
  isLoading 
}) => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    if (character && isOpen) {
      form.setFieldsValue({
        name: character.name,
        gender: character.gender,
        description: character.description,
        displayedDescription: character.displayedDescription,
      });
      setTags(character.tags || []);
    }
  }, [character, isOpen, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Validate required fields
      if (!values.name?.trim()) {
        form.setFields([{
          name: 'name',
          errors: ['Vui lòng nhập tên nhân vật!']
        }]);
        return;
      }

      if (!values.gender) {
        form.setFields([{
          name: 'gender',
          errors: ['Vui lòng chọn giới tính!']
        }]);
        return;
      }
      
      // Validate main character name - MUST be {{userName}}
      if (character?.type === 'main' && values.name.trim() !== '{{userName}}') {
        form.setFields([{
          name: 'name',
          errors: ['Nhân vật chính phải có tên là {{userName}}']
        }]);
        return;
      }
      
      const updateData: UpdateCharacterRequest = {
        name: values.name.trim(),
        gender: values.gender,
        type: (character?.type as 'main' | 'npc') || 'npc',
        tags: tags,
        description: values.description?.trim() || undefined,
        displayedDescription: values.displayedDescription?.trim() || undefined,
      };

      await onSubmit(updateData);
      onClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleTagChange = (value: string[]) => {
    setTags(value);
  };

  return (
    <Modal
      title="Cập nhật thông tin nhân vật"
      open={isOpen}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Hủy
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit}
          loading={isLoading}
        >
          Cập nhật
        </Button>,
      ]}
    >
      {character && (
        <Form form={form} layout="vertical">
          <Form.Item 
            label="Tên nhân vật" 
            name="name"
            rules={[{ required: true, message: 'Vui lòng nhập tên nhân vật!' }]}
          >
            <Input 
              placeholder={character.type === 'main' ? '{{userName}}' : 'Nhập tên nhân vật'}
            />
          </Form.Item>
          
          {character.type === 'main' && (
            <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '6px' }}>
              <Text type="warning">
                <strong>⚠️ Bắt buộc:</strong> Nhân vật chính phải có tên là {'{{userName}}'} để hệ thống tự động thay thế bằng tên người chơi
              </Text>
            </div>
          )}

          <Form.Item 
            label="Giới tính" 
            name="gender"
            rules={[{ required: true, message: 'Vui lòng chọn giới tính!' }]}
          >
            <Select placeholder="Chọn giới tính">
              <Option value="male">Nam</Option>
              <Option value="female">Nữ</Option>
              <Option value="other">Khác</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Tags">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Nhập tags (có thể tạo mới)"
              value={tags}
              onChange={handleTagChange}
            />
          </Form.Item>

          <Form.Item label="Mô tả chi tiết" name="description">
            <TextArea 
              rows={3}
              placeholder="Mô tả chi tiết về nhân vật"
            />
          </Form.Item>

          <Form.Item label="Mô tả hiển thị" name="displayedDescription">
            <TextArea 
              rows={2}
              placeholder="Mô tả ngắn hiển thị cho người chơi"
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

interface ActivateStateModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: CharacterStateWithRelationships | null;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

const ActivateStateModal: React.FC<ActivateStateModalProps> = ({ 
  isOpen, 
  onClose, 
  state, 
  onConfirm, 
  isLoading 
}) => {
  return (
    <Modal
      title="Kích hoạt State"
      open={isOpen && !!state}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Hủy
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          onClick={onConfirm}
          loading={isLoading}
          danger
        >
          Kích hoạt
        </Button>,
      ]}
    >
      {state && (
        <>
          <Text>Bạn có chắc chắn muốn kích hoạt state này?</Text>
          <Card style={{ marginTop: 16, backgroundColor: '#f5f5f5' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Tên">{state.name}</Descriptions.Item>
              <Descriptions.Item label="Tuổi">{state.age}</Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                <Tag color={state.status === 'draft' ? 'orange' : 'green'}>
                  {state.status}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      )}
    </Modal>
  );
};

const CharacterDetail: React.FC = () => {
  const { lifeTreeId, characterId } = useParams<{ lifeTreeId: string; characterId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [character, setCharacter] = useState<CharacterDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [methodSelectionModalOpen, setMethodSelectionModalOpen] = useState(false);
  const [createStateModalOpen, setCreateStateModalOpen] = useState(false);
  const [aiDescriptionModalOpen, setAIDescriptionModalOpen] = useState(false);
  const [updateCharacterModalOpen, setUpdateCharacterModalOpen] = useState(false);

  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [selectedState, setSelectedState] = useState<CharacterStateWithRelationships | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [stateInitialData, setStateInitialData] = useState<Partial<CreateCharacterStateRequest> | undefined>(undefined);

  useEffect(() => {
    if (!lifeTreeId || !characterId) {
      navigate('/lifetrees');
      return;
    }
    
    loadCharacterDetail();
  }, [lifeTreeId, characterId, navigate]);

  const loadCharacterDetail = async () => {
    try {
      setLoading(true);
      const data = await lifetreeApi.getCharacterDetail(lifeTreeId!, characterId!);
      setCharacter(data);
    } catch (error) {
      console.error('Error loading character detail:', error);
      showToast('error', 'Có lỗi xảy ra khi tải thông tin nhân vật');
      navigate('/lifetrees');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStateClick = () => {
    setMethodSelectionModalOpen(true);
  };

  const handleMethodSelection = (method: 'manual' | 'ai') => {
    setMethodSelectionModalOpen(false);
    if (method === 'manual') {
      setStateInitialData(undefined); // Clear any previous data
      setCreateStateModalOpen(true);
    } else {
      setAIDescriptionModalOpen(true);
    }
  };

  const handleAICompletion = async (description: string, previousStateId?: string) => {
    try {
      setActionLoading(prev => ({ ...prev, aiCompletion: true }));
      
      let aiResult;
      if (previousStateId) {
        // Gọi API tạo next state completion
        aiResult = await lifetreeApi.createCharacterNextStateCompletion(lifeTreeId!, characterId!, { 
          description, 
          previousStateId 
        });
      } else {
        // Gọi API tạo state completion thông thường
        aiResult = await lifetreeApi.createCharacterStateCompletion(lifeTreeId!, characterId!, { description });
      }
      
      // Convert AI result to form data format
      const initialData: Partial<CreateCharacterStateRequest> = {
        name: aiResult.name,
        age: aiResult.age,
        alias: aiResult.alias,
        occupation: aiResult.occupation,
        appearance: aiResult.appearance,
        innerSelf: aiResult.innerSelf,
        relationshipStyles: aiResult.relationshipStyles,
        description: aiResult.description,
        displayedDescription: aiResult.displayedDescription,
      };
      
      setStateInitialData(initialData);
      setAIDescriptionModalOpen(false);
      setCreateStateModalOpen(true);
      showToast('success', 'AI đã tạo nguyên mẫu thành công! Vui lòng kiểm tra và chỉnh sửa nếu cần.');
    } catch (error: any) {
      const errorMsg = error?.response?.data?.messages?.[0] || error?.message || 'Có lỗi xảy ra khi tạo nguyên mẫu bằng AI';
      showToast('error', errorMsg);
      console.error('Error creating AI completion:', error);
      throw error;
    } finally {
      setActionLoading(prev => ({ ...prev, aiCompletion: false }));
    }
  };

  const handleCreateState = async (data: CreateCharacterStateRequest) => {
    try {
      setActionLoading(prev => ({ ...prev, createState: true }));
      await lifetreeApi.createCharacterState(lifeTreeId!, characterId!, data);
      showToast('success', 'Tạo state thành công');
      setCreateStateModalOpen(false);
      setStateInitialData(undefined); // Clear initial data
      await loadCharacterDetail();
    } catch (error) {
      console.error('Error creating state:', error);
      throw error;
    } finally {
      setActionLoading(prev => ({ ...prev, createState: false }));
    }
  };

  const handleUpdateCharacter = async (data: UpdateCharacterRequest) => {
    try {
      setActionLoading(prev => ({ ...prev, updateCharacter: true }));
      await lifetreeApi.updateCharacter(lifeTreeId!, characterId!, data);
      showToast('success', 'Cập nhật nhân vật thành công');
      setUpdateCharacterModalOpen(false);
      await loadCharacterDetail();
    } catch (error) {
      console.error('Error updating character:', error);
      showToast('error', 'Có lỗi xảy ra khi cập nhật nhân vật');
    } finally {
      setActionLoading(prev => ({ ...prev, updateCharacter: false }));
    }
  };

  const handleActivateState = async (state: CharacterStateWithRelationships) => {
    setSelectedState(state);
    setActivateModalOpen(true);
  };

  const confirmActivateState = async () => {
    if (!selectedState) return;
    
    try {
      setActionLoading(prev => ({ ...prev, activate: true }));
      await lifetreeApi.activateState(lifeTreeId!, characterId!, selectedState.id);
      showToast('success', 'Kích hoạt state thành công');
      setActivateModalOpen(false);
      await loadCharacterDetail();
    } catch (error) {
      console.error('Error activating state:', error);
      showToast('error', 'Có lỗi xảy ra khi kích hoạt state');
    } finally {
      setActionLoading(prev => ({ ...prev, activate: false }));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!character) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Empty description="Không tìm thấy nhân vật" />
        <Button 
          type="primary"
          onClick={() => navigate('/lifetrees')}
          style={{ marginTop: 16 }}
        >
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(`/lifetrees/${lifeTreeId}`)}
            >
              Quay lại Lifetree
            </Button>
          </Col>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              Chi tiết nhân vật
            </Title>
          </Col>
        </Row>
        <Breadcrumb style={{ marginTop: 16 }}>
          <Breadcrumb.Item>
            <Button type="link" onClick={() => navigate('/lifetrees')} style={{ padding: 0 }}>
              Lifetrees
            </Button>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Button type="link" onClick={() => navigate(`/lifetrees/${lifeTreeId}`)} style={{ padding: 0 }}>
              Lifetree Detail
            </Button>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Character Detail</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Card 
        title="Thông tin cơ bản"
        extra={
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => setUpdateCharacterModalOpen(true)}
          >
            Cập nhật
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Tên">{character.name}</Descriptions.Item>
              <Descriptions.Item label="Giới tính">{character.gender}</Descriptions.Item>
              <Descriptions.Item label="Loại">
                <Tag color={character.type === 'main' ? 'gold' : 'blue'}>
                  {character.type === 'main' ? 'Nhân vật chính' : 'NPC'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                <Tag color={
                  character.status === 'active' ? 'green' :
                  character.status === 'draft' ? 'orange' : 'red'
                }>
                  {character.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            {character.imageUrl && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Ảnh đại diện:</Text>
                <div style={{ marginTop: 8 }}>
                  <Image 
                    width={128} 
                    height={128}
                    src={character.imageUrl} 
                    alt={character.name}
                    style={{ objectFit: 'cover', objectPosition: 'top center', borderRadius: 8 }}
                  />
                </div>
              </div>
            )}
            <div>
              <Text strong>Tags:</Text>
              <div style={{ marginTop: 8 }}>
                {character.tags.map((tag, index) => (
                  <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>
          </Col>
        </Row>
        {character.description && (
          <div style={{ marginTop: 16 }}>
            <Text strong>Mô tả:</Text>
            <div style={{ marginTop: 8 }}>
              <Text>{character.description}</Text>
            </div>
          </div>
        )}
      </Card>

      <Card 
        title="Danh sách State"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAddStateClick}
          >
            Thêm State
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <List
          itemLayout="horizontal"
          dataSource={character.states}
          renderItem={(state) => (
            <List.Item
              actions={[
                state.status === 'draft' && (
                  <Tooltip title="Kích hoạt state" key="activate">
                    <Button 
                      type="primary" 
                      icon={<CheckCircleOutlined />} 
                      size="small"
                      onClick={() => handleActivateState(state)}
                      loading={actionLoading.activate}
                    >
                      Kích hoạt
                    </Button>
                  </Tooltip>
                ),
                <Tooltip title="Xem chi tiết state" key="detail">
                  <Button 
                    icon={<EyeOutlined />} 
                    size="small"
                    onClick={() => navigate(`/lifetrees/${lifeTreeId}/characters/${characterId}/states/${state.id}`)}
                  >
                    Chi tiết
                  </Button>
                </Tooltip>
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={
                  state.imageUrl ? (
                    <Avatar 
                      src={state.imageUrl}
                      shape="square"
                      style={{ 
                        width: 80,
                        height: 120,
                        objectFit: 'cover',
                        objectPosition: 'top center',
                      }}
                    />
                  ) : (
                    <Avatar 
                      icon={<UserOutlined />}
                      shape="square"
                      style={{ 
                        width: 80,
                        height: 120,
                        backgroundColor: '#f0f0f0',
                        color: '#999',
                      }}
                    />
                  )
                }
                title={
                  <Space>
                    <Text strong>{state.name}</Text>
                    <Tag color={
                      state.status === 'active' ? 'green' :
                      state.status === 'draft' ? 'orange' : 'red'
                    }>
                      {state.status.toUpperCase()}
                    </Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    <Text type="secondary">Tuổi: {state.age}</Text>
                    {state.alias && (
                      <Text type="secondary">Biệt danh: {state.alias}</Text>
                    )}
                    {state.occupation && (
                      <Text type="secondary">Nghề: {state.occupation}</Text>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <UpdateCharacterModal
        isOpen={updateCharacterModalOpen}
        onClose={() => setUpdateCharacterModalOpen(false)}
        character={character}
        onSubmit={handleUpdateCharacter}
        isLoading={actionLoading.updateCharacter || false}
      />

      <MethodSelectionModal
        isOpen={methodSelectionModalOpen}
        onClose={() => setMethodSelectionModalOpen(false)}
        onSelectMethod={handleMethodSelection}
      />

      <AIDescriptionModal
        isOpen={aiDescriptionModalOpen}
        onClose={() => setAIDescriptionModalOpen(false)}
        onSubmit={handleAICompletion}
        isLoading={actionLoading.aiCompletion || false}
        characterName={character?.name || ''}
        availableStates={character?.states || []}
      />

      <CreateStateModal
        isOpen={createStateModalOpen}
        onClose={() => {
          setCreateStateModalOpen(false);
          setStateInitialData(undefined); // Clear initial data when closing
        }}
        onSubmit={handleCreateState}
        isLoading={actionLoading.createState || false}
        initialData={stateInitialData}
        characterName={character?.name || ''}
      />

      <ActivateStateModal
        isOpen={activateModalOpen}
        onClose={() => setActivateModalOpen(false)}
        state={selectedState}
        onConfirm={confirmActivateState}
        isLoading={actionLoading.activate || false}
      />
    </div>
  );
};

export default CharacterDetail; 